import React, { useEffect, useState } from 'react';

import { formatEndDate } from './contractDashboardHelpers';

import useApiRequest from 'Hooks/useApiRequest';

/**
 * IB-703 — "Contract end dates · ended & coming up". Lists expenditure/revenue contracts with a
 * term end date (workforce excluded), sorted ascending, each tagged ended / ending-soon / future.
 * The "≤{window} days" pill is clickable: it reveals an inline number input that PUTs the new
 * window to the event and refetches the dashboard.
 */
const StatusPill = ({ className, dotClass, children }) => (
    <span className={`tw-inline-flex tw-items-center tw-gap-1.5 tw-rounded-full tw-px-3 tw-py-1 tw-text-xs tw-font-medium ${className}`}>
        <span className={`tw-h-1.5 tw-w-1.5 tw-shrink-0 tw-rounded-full ${dotClass}`} />
        {children}
    </span>
);

const StatusCell = ({ item }) => {
    if (item.status === 'ended') {
        return (
            <StatusPill className="tw-bg-charcoal-15 tw-text-charcoal-75" dotClass="tw-bg-charcoal-50">
                {$t('Ended {{date}}', { date: formatEndDate(item.end_date) })}
            </StatusPill>
        );
    }

    if (item.status === 'ending_soon') {
        return (
            <StatusPill className="tw-bg-red-25 tw-text-red" dotClass="tw-bg-red">
                {$t('Ends in {{days}}d', { days: item.days_remaining })}
            </StatusPill>
        );
    }

    return <span className="tw-text-sm tw-text-charcoal-75">{formatEndDate(item.end_date)}</span>;
};

const EndDateRow = ({ item }) => (
    <div className="tw-flex tw-items-center tw-justify-between tw-gap-3 tw-py-3 tw-border-b tw-border-grey last:tw-border-b-0">
        <div className="tw-flex tw-flex-col tw-min-w-0">
            <span className="tw-text-sm tw-font-medium tw-text-charcoal tw-truncate">{item.supplier || $t('Unnamed supplier')}</span>
            <span className="tw-text-xs tw-text-charcoal-50 tw-truncate">{`${item.reference ?? ''} · ${item.type}`}</span>
        </div>
        <div className="tw-shrink-0 tw-text-end">
            <StatusCell item={item} />
        </div>
    </div>
);

const ContractEndDates = ({ endDates, updateWindowUrl, onWindowUpdated }) => {
    const endingSoonWindow = endDates?.window ?? 90;
    const endedCount = endDates?.ended_count ?? 0;
    const endingSoonCount = endDates?.ending_soon_count ?? 0;
    const items = endDates?.items ?? [];

    const [isEditing, setIsEditing] = useState(false);
    const [draftWindow, setDraftWindow] = useState(endingSoonWindow);

    const { put, loading } = useApiRequest({
        url: updateWindowUrl,
        successMessage: $t('Ending-soon window updated'),
    });

    useEffect(() => {
        setDraftWindow(endingSoonWindow);
    }, [endingSoonWindow]);

    const saveWindow = () => {
        const days = parseInt(draftWindow, 10);

        if (!Number.isInteger(days) || days < 1) {
            return;
        }

        put({ days })
            .then(() => {
                setIsEditing(false);
                if (typeof onWindowUpdated === 'function') {
                    onWindowUpdated();
                }
            })
            .catch(() => {});
    };

    return (
        <div className="tw-rounded-lg tw-bg-white tw-shadow-primary tw-px-6 tw-py-6.5">
            <div className="tw-flex tw-items-baseline tw-gap-2 tw-mb-4">
                <h4 className="tw-text-base tw-font-semibold tw-text-charcoal">{$t('Contract end dates')}</h4>
                <span className="tw-text-sm tw-text-charcoal-50">{$t('· ended & coming up')}</span>
            </div>

            <div className="tw-flex tw-flex-wrap tw-items-center tw-gap-2 tw-mb-4">
                <span className="tw-rounded-full tw-bg-charcoal-15 tw-px-3 tw-py-1 tw-text-xs tw-font-medium tw-text-charcoal-75">
                    {$t('{{count}} ended', { count: endedCount })}
                </span>

                {isEditing ? (
                    <span className="tw-flex tw-items-center tw-gap-1 tw-rounded-full tw-bg-red-25 tw-px-3 tw-py-1">
                        <span className="tw-text-xs tw-font-medium tw-text-red">{$t('ending ≤')}</span>
                        <input
                            type="number"
                            min="1"
                            value={draftWindow}
                            disabled={loading}
                            onChange={(event) => setDraftWindow(event.target.value)}
                            onKeyDown={(event) => {
                                if (event.key === 'Enter') {
                                    saveWindow();
                                }
                                if (event.key === 'Escape') {
                                    setIsEditing(false);
                                }
                            }}
                            className="tw-w-14 tw-rounded tw-border tw-border-red-50 tw-px-1 tw-py-0 tw-text-xs tw-text-charcoal"
                        />
                        <span className="tw-text-xs tw-font-medium tw-text-red">{$t('days')}</span>
                        <button type="button" onClick={saveWindow} disabled={loading} className="tw-text-xs tw-font-semibold tw-text-red tw-underline">
                            {$t('Save')}
                        </button>
                    </span>
                ) : (
                    <button
                        type="button"
                        onClick={() => setIsEditing(true)}
                        className="tw-rounded-full tw-bg-red-25 tw-px-3 tw-py-1 tw-text-xs tw-font-medium tw-text-red hover:tw-bg-red-50"
                    >
                        {$t('{{count}} ending ≤{{window}} days', { count: endingSoonCount, window: endingSoonWindow })}
                    </button>
                )}
            </div>

            {items.length === 0 ? (
                <p className="tw-text-sm tw-text-charcoal-50">{$t('none ending soon')}</p>
            ) : (
                <div className="tw-flex tw-flex-col">
                    {items.map((item, index) => (
                        <EndDateRow key={`${item.reference ?? 'ref'}-${index}`} item={item} />
                    ))}
                </div>
            )}
        </div>
    );
};

export default ContractEndDates;
